import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/integrations/supabase/types';
import {
  getPortalAuthStorageKey,
  type AuthPortal,
} from '@/integrations/supabase/portalClientFactory';
import {
  getGoogleAuthCallbackUrl,
  getGoogleOAuthQueryParams,
} from '@/lib/googleAuth';
import { sanitizeForkSeekReturnTo } from '@/lib/seekAuthReturn';
import { getSupabaseBaseUrl } from '@/lib/selfHostedUrls';

type OAuthPortal = Extract<AuthPortal, 'hire' | 'seek'>;
type OAuthClient = Pick<SupabaseClient<Database>, 'auth'>;

const ATTEMPT_VERSION = 1;
const ATTEMPT_TTL_MS = 10 * 60 * 1_000;
const ATTEMPT_STORAGE_PREFIX = 'fork:oauth-attempt:v1:';
const OAUTH_NONCE_PARAMETER = 'oauth_attempt';
const CALLBACK_PATHS: Record<OAuthPortal, string> = {
  hire: '/hire/auth/callback',
  seek: '/seek/auth/callback',
};

interface OAuthAttempt {
  version: typeof ATTEMPT_VERSION;
  portal: OAuthPortal;
  callbackOrigin: string;
  callbackPath: string;
  continuation: string | null;
  nonce: string;
  verifierFingerprint: string;
  createdAt: number;
  expiresAt: number;
}

interface BrowserDependencies {
  transactionStorage: Storage;
  now: () => number;
  randomBytes: (length: number) => Uint8Array;
  fingerprintVerifier: (value: string) => Promise<string>;
  navigateExternal: (url: string) => void;
  replaceHistory: (path: string) => void;
  callbackUrl: (portal: OAuthPortal) => string;
  supabaseUrl: string;
}

export type PortalOAuthFailureReason =
  | 'active_attempt_exists'
  | 'callback_mismatch'
  | 'callback_scrub_failed'
  | 'exchange_failed'
  | 'invalid_continuation'
  | 'invalid_provider_url'
  | 'missing_attempt'
  | 'missing_callback_parameters'
  | 'missing_pkce_verifier'
  | 'oauth_initiation_failed'
  | 'oauth_storage_unavailable'
  | 'orphaned_pkce_verifier'
  | 'provider_error'
  | 'replayed_or_stale_attempt'
  | 'unexpected_callback_parameters';

export type PortalOAuthInitiationResult =
  | { ok: true }
  | { ok: false; reason: PortalOAuthFailureReason };

export type PortalOAuthCompletionResult =
  | { ok: true; continuation: string | null }
  | { ok: false; reason: PortalOAuthFailureReason };

export function rejectPortalGoogleOAuthCallback(
  portal: OAuthPortal,
  dependencyOverrides?: Partial<BrowserDependencies>,
) {
  const dependencies = getDependencies(dependencyOverrides);
  try {
    dependencies.replaceHistory(CALLBACK_PATHS[portal]);
  } catch {
    clearAttempt(portal, dependencies, true);
    return false;
  }
  return clearAttempt(portal, dependencies, true);
}

function getDefaultDependencies(): BrowserDependencies {
  return {
    transactionStorage: window.sessionStorage,
    now: () => Date.now(),
    randomBytes: (length) => crypto.getRandomValues(new Uint8Array(length)),
    fingerprintVerifier: async (value) => {
      const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
      return toBase64Url(new Uint8Array(digest));
    },
    navigateExternal: (url) => window.location.assign(url),
    replaceHistory: (path) => window.history.replaceState(window.history.state, '', path),
    callbackUrl: (portal) => getGoogleAuthCallbackUrl(portal),
    supabaseUrl: getSupabaseBaseUrl(),
  };
}

function getDependencies(overrides?: Partial<BrowserDependencies>): BrowserDependencies {
  return { ...getDefaultDependencies(), ...overrides };
}

function attemptStorageKey(portal: OAuthPortal) {
  return `${ATTEMPT_STORAGE_PREFIX}${portal}`;
}

function verifierStorageKey(portal: OAuthPortal, supabaseUrl: string) {
  return `${getPortalAuthStorageKey(portal, supabaseUrl)}-code-verifier`;
}

function removeStorageItem(storage: Storage, key: string) {
  try {
    storage.removeItem(key);
    return storage.getItem(key) === null;
  } catch {
    return false;
  }
}

function setStorageItem(storage: Storage, key: string, value: string) {
  try {
    storage.setItem(key, value);
    return storage.getItem(key) === value;
  } catch {
    return false;
  }
}

function clearAttempt(
  portal: OAuthPortal,
  dependencies: BrowserDependencies,
  includeVerifier: boolean,
) {
  const attemptRemoved = removeStorageItem(
    dependencies.transactionStorage,
    attemptStorageKey(portal),
  );
  let verifierRemoved = true;
  if (includeVerifier) {
    verifierRemoved = removeStorageItem(
      dependencies.transactionStorage,
      verifierStorageKey(portal, dependencies.supabaseUrl),
    );
  }
  return attemptRemoved && verifierRemoved;
}

function toBase64Url(bytes: Uint8Array) {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/u, '');
}

function canonicalizeContinuation(portal: OAuthPortal, value?: string | null) {
  if (portal === 'hire') {
    return value === undefined || value === null || value === '/dashboard'
      ? '/dashboard'
      : undefined;
  }

  if (value === undefined || value === null || value === '') return null;
  return sanitizeForkSeekReturnTo(value, 'oauth_completion') ?? undefined;
}

function parseAttempt(raw: string | null): OAuthAttempt | null {
  if (!raw) return null;

  try {
    const value = JSON.parse(raw) as Partial<OAuthAttempt>;
    if (
      value.version !== ATTEMPT_VERSION ||
      (value.portal !== 'hire' && value.portal !== 'seek') ||
      typeof value.callbackOrigin !== 'string' ||
      typeof value.callbackPath !== 'string' ||
      (typeof value.continuation !== 'string' && value.continuation !== null) ||
      typeof value.nonce !== 'string' ||
      typeof value.verifierFingerprint !== 'string' ||
      typeof value.createdAt !== 'number' ||
      typeof value.expiresAt !== 'number'
    ) {
      return null;
    }

    return value as OAuthAttempt;
  } catch {
    return null;
  }
}

function readAttempt(portal: OAuthPortal, storage: Storage) {
  try {
    const raw = storage.getItem(attemptStorageKey(portal));
    return {
      accessible: true as const,
      present: raw !== null,
      raw,
      attempt: parseAttempt(raw),
    };
  } catch {
    return { accessible: false as const, present: false, raw: null, attempt: null };
  }
}

function claimAttempt(portal: OAuthPortal, storage: Storage, expectedRaw: string) {
  const key = attemptStorageKey(portal);
  try {
    if (storage.getItem(key) !== expectedRaw) return 'changed' as const;
    storage.removeItem(key);
    return storage.getItem(key) === null ? 'claimed' as const : 'unavailable' as const;
  } catch {
    return 'unavailable' as const;
  }
}

function readVerifier(portal: OAuthPortal, dependencies: BrowserDependencies) {
  try {
    const value = dependencies.transactionStorage.getItem(
      verifierStorageKey(portal, dependencies.supabaseUrl),
    );
    return typeof value === 'string' && value.length > 0 ? value : null;
  } catch {
    return null;
  }
}

function parseExpectedCallback(portal: OAuthPortal, callbackValue: string) {
  const callback = new URL(callbackValue);
  if (
    callback.username ||
    callback.password ||
    callback.pathname !== CALLBACK_PATHS[portal] ||
    callback.search ||
    callback.hash
  ) {
    throw new Error('oauth_callback_configuration_invalid');
  }
  return callback;
}

export async function initiatePortalGoogleOAuth(
  client: OAuthClient,
  options: {
    portal: OAuthPortal;
    continuation?: string | null;
    dependencies?: Partial<BrowserDependencies>;
  },
): Promise<PortalOAuthInitiationResult> {
  const dependencies = getDependencies(options.dependencies);
  const continuation = canonicalizeContinuation(options.portal, options.continuation);
  if (continuation === undefined) return { ok: false, reason: 'invalid_continuation' };

  const stored = readAttempt(options.portal, dependencies.transactionStorage);
  if (!stored.accessible) return { ok: false, reason: 'oauth_storage_unavailable' };
  if (stored.present && !stored.attempt) {
    return clearAttempt(options.portal, dependencies, true)
      ? { ok: false, reason: 'replayed_or_stale_attempt' }
      : { ok: false, reason: 'oauth_storage_unavailable' };
  }

  const now = dependencies.now();
  if (stored.attempt && stored.attempt.expiresAt > now) {
    return { ok: false, reason: 'active_attempt_exists' };
  }
  if (stored.attempt && !clearAttempt(options.portal, dependencies, true)) {
    return { ok: false, reason: 'oauth_storage_unavailable' };
  }

  if (readVerifier(options.portal, dependencies)) {
    return clearAttempt(options.portal, dependencies, true)
      ? { ok: false, reason: 'orphaned_pkce_verifier' }
      : { ok: false, reason: 'oauth_storage_unavailable' };
  }

  let callback: URL;
  try {
    callback = parseExpectedCallback(options.portal, dependencies.callbackUrl(options.portal));
  } catch {
    return { ok: false, reason: 'callback_mismatch' };
  }

  let nonce: string;
  try {
    nonce = toBase64Url(dependencies.randomBytes(32));
  } catch {
    return { ok: false, reason: 'oauth_initiation_failed' };
  }
  if (!/^[A-Za-z0-9_-]{43}$/u.test(nonce)) {
    return { ok: false, reason: 'oauth_initiation_failed' };
  }

  const redirectTo = new URL(callback.toString());
  redirectTo.searchParams.set(OAUTH_NONCE_PARAMETER, nonce);
  const attempt: OAuthAttempt = {
    version: ATTEMPT_VERSION,
    portal: options.portal,
    callbackOrigin: callback.origin,
    callbackPath: callback.pathname,
    continuation,
    nonce,
    verifierFingerprint: '',
    createdAt: now,
    expiresAt: now + ATTEMPT_TTL_MS,
  };

  if (!setStorageItem(
    dependencies.transactionStorage,
    attemptStorageKey(options.portal),
    JSON.stringify(attempt),
  )) {
    return { ok: false, reason: 'oauth_storage_unavailable' };
  }

  let oauthResponse: Awaited<ReturnType<OAuthClient['auth']['signInWithOAuth']>>;
  try {
    oauthResponse = await client.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectTo.toString(),
        queryParams: getGoogleOAuthQueryParams(),
        skipBrowserRedirect: true,
      },
    });
  } catch {
    clearAttempt(options.portal, dependencies, true);
    return { ok: false, reason: 'oauth_initiation_failed' };
  }

  const { data, error } = oauthResponse;

  if (error) {
    clearAttempt(options.portal, dependencies, true);
    return { ok: false, reason: 'oauth_initiation_failed' };
  }
  const verifier = readVerifier(options.portal, dependencies);
  if (!verifier) {
    clearAttempt(options.portal, dependencies, true);
    return { ok: false, reason: 'missing_pkce_verifier' };
  }

  try {
    attempt.verifierFingerprint = await dependencies.fingerprintVerifier(verifier);
    if (!attempt.verifierFingerprint || !setStorageItem(
      dependencies.transactionStorage,
      attemptStorageKey(options.portal),
      JSON.stringify(attempt),
    )) throw new Error('attempt_storage_failed');
  } catch {
    clearAttempt(options.portal, dependencies, true);
    return { ok: false, reason: 'oauth_storage_unavailable' };
  }

  let providerUrl: URL;
  try {
    providerUrl = new URL(data.url ?? '');
    const expectedAuthorizeUrl = new URL('/auth/v1/authorize', `${dependencies.supabaseUrl}/`);
    if (
      providerUrl.origin !== expectedAuthorizeUrl.origin ||
      providerUrl.pathname !== expectedAuthorizeUrl.pathname ||
      providerUrl.username ||
      providerUrl.password ||
      providerUrl.hash ||
      providerUrl.searchParams.getAll('provider').length !== 1 ||
      providerUrl.searchParams.get('provider') !== 'google'
    ) {
      throw new Error('invalid_provider_url');
    }
  } catch {
    clearAttempt(options.portal, dependencies, true);
    return { ok: false, reason: 'invalid_provider_url' };
  }

  try {
    dependencies.navigateExternal(providerUrl.toString());
  } catch {
    clearAttempt(options.portal, dependencies, true);
    return { ok: false, reason: 'oauth_initiation_failed' };
  }
  return { ok: true };
}

function getSingleParameter(url: URL, key: string) {
  const values = url.searchParams.getAll(key);
  return values.length === 1 && values[0] ? values[0] : null;
}

function callbackHasOnlyCanonicalAllowedParameters(url: URL) {
  const rawQuery = url.search.startsWith('?') ? url.search.slice(1) : url.search;
  if (!rawQuery) return false;

  const rawEntries = rawQuery.split('&');
  const parsedEntries = [...url.searchParams.entries()];
  if (rawEntries.length !== 2 || parsedEntries.length !== 2) return false;

  const seen = new Set<string>();
  for (let index = 0; index < rawEntries.length; index += 1) {
    const separator = rawEntries[index].indexOf('=');
    if (separator <= 0) return false;
    const rawName = rawEntries[index].slice(0, separator);
    const rawValue = rawEntries[index].slice(separator + 1);
    const [name, value] = parsedEntries[index];
    if (
      rawName !== name ||
      rawValue !== value ||
      (name !== 'code' && name !== OAUTH_NONCE_PARAMETER) ||
      seen.has(name)
    ) return false;
    seen.add(name);
  }

  return seen.has('code') && seen.has(OAUTH_NONCE_PARAMETER);
}

function isCanonicalCode(value: string) {
  return value.length <= 2_048 && ![...value].some((character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    return codePoint <= 0x20 || (codePoint >= 0x7f && codePoint <= 0x9f) || /\s/u.test(character);
  });
}

function hasCanonicalRawCallbackLocation(rawCallback: string, expected: URL) {
  const queryIndex = rawCallback.indexOf('?');
  if (queryIndex <= 0) return false;

  // Compare the pre-query bytes rather than URL-normalized fields. URL parsing
  // resolves dot segments and canonicalizes aliases that must never be accepted
  // as a second spelling of the configured callback route.
  return rawCallback.slice(0, queryIndex) === `${expected.origin}${expected.pathname}`;
}

async function revokeExchangedLocalSession(client: OAuthClient) {
  try {
    await client.auth.signOut({ scope: 'local' });
  } catch {
    // The caller still fails closed; sign-out errors must not expose or revive
    // callback material.
  }
}

export async function completePortalGoogleOAuth(
  client: OAuthClient,
  options: {
    portal: OAuthPortal;
    currentUrl?: string;
    dependencies?: Partial<BrowserDependencies>;
  },
): Promise<PortalOAuthCompletionResult> {
  const dependencies = getDependencies(options.dependencies);
  const rawCallback = options.currentUrl ?? window.location.href;
  let callback: URL;
  let expected: URL;

  try {
    callback = new URL(rawCallback);
  } catch {
    return { ok: false, reason: 'callback_mismatch' };
  }

  // Retain the one-time values only in local variables and remove them from
  // browser history before validation or network exchange.
  try {
    dependencies.replaceHistory(CALLBACK_PATHS[options.portal]);
  } catch {
    clearAttempt(options.portal, dependencies, true);
    return { ok: false, reason: 'callback_scrub_failed' };
  }

  try {
    expected = parseExpectedCallback(options.portal, dependencies.callbackUrl(options.portal));
  } catch {
    clearAttempt(options.portal, dependencies, true);
    return { ok: false, reason: 'callback_mismatch' };
  }

  if (
    !hasCanonicalRawCallbackLocation(rawCallback, expected) ||
    callback.origin !== expected.origin ||
    callback.pathname !== expected.pathname ||
    callback.username ||
    callback.password ||
    callback.hash
  ) {
    clearAttempt(options.portal, dependencies, true);
    return { ok: false, reason: 'callback_mismatch' };
  }

  if (callback.searchParams.has('error')) {
    clearAttempt(options.portal, dependencies, true);
    return { ok: false, reason: 'provider_error' };
  }
  if (!callbackHasOnlyCanonicalAllowedParameters(callback)) {
    clearAttempt(options.portal, dependencies, true);
    return { ok: false, reason: 'unexpected_callback_parameters' };
  }

  const code = getSingleParameter(callback, 'code');
  const nonce = getSingleParameter(callback, OAUTH_NONCE_PARAMETER);
  if (!code || !nonce || !isCanonicalCode(code) || !/^[A-Za-z0-9_-]{43}$/u.test(nonce)) {
    clearAttempt(options.portal, dependencies, true);
    return { ok: false, reason: 'missing_callback_parameters' };
  }

  const stored = readAttempt(options.portal, dependencies.transactionStorage);
  if (!stored.accessible) {
    clearAttempt(options.portal, dependencies, true);
    return { ok: false, reason: 'oauth_storage_unavailable' };
  }
  const attempt = stored.attempt;
  if (!attempt) {
    // A concurrently completing callback may already have claimed the attempt
    // in this tab. Never remove its verifier. A genuinely orphaned verifier is
    // rejected and cleaned by the next initiation attempt.
    return { ok: false, reason: 'missing_attempt' };
  }

  const canonicalContinuation = canonicalizeContinuation(options.portal, attempt.continuation);
  const now = dependencies.now();
  if (
    attempt.portal !== options.portal ||
    attempt.callbackOrigin !== expected.origin ||
    attempt.callbackPath !== expected.pathname ||
    attempt.nonce !== nonce ||
    attempt.createdAt > now ||
    attempt.expiresAt <= now ||
    attempt.expiresAt - attempt.createdAt !== ATTEMPT_TTL_MS ||
    canonicalContinuation === undefined ||
    canonicalContinuation !== attempt.continuation
  ) {
    clearAttempt(options.portal, dependencies, true);
    return { ok: false, reason: 'replayed_or_stale_attempt' };
  }

  const verifier = readVerifier(options.portal, dependencies);
  if (!verifier) {
    clearAttempt(options.portal, dependencies, true);
    return { ok: false, reason: 'missing_pkce_verifier' };
  }

  if (!stored.raw) {
    clearAttempt(options.portal, dependencies, true);
    return { ok: false, reason: 'missing_attempt' };
  }

  // Claim synchronously before the first await. JavaScript callbacks in this
  // tab cannot interleave the compare/remove operation, while another tab has
  // an independent sessionStorage transaction and verifier.
  const claim = claimAttempt(options.portal, dependencies.transactionStorage, stored.raw);
  if (claim === 'changed') {
    return { ok: false, reason: 'replayed_or_stale_attempt' };
  }
  if (claim === 'unavailable') {
    return { ok: false, reason: 'oauth_storage_unavailable' };
  }

  try {
    if (
      !attempt.verifierFingerprint ||
      await dependencies.fingerprintVerifier(verifier) !== attempt.verifierFingerprint
    ) {
      clearAttempt(options.portal, dependencies, true);
      return { ok: false, reason: 'missing_pkce_verifier' };
    }
  } catch {
    clearAttempt(options.portal, dependencies, true);
    return { ok: false, reason: 'missing_pkce_verifier' };
  }

  let exchangeResponse: Awaited<ReturnType<OAuthClient['auth']['exchangeCodeForSession']>>;
  try {
    exchangeResponse = await client.auth.exchangeCodeForSession(code);
  } catch {
    clearAttempt(options.portal, dependencies, true);
    return { ok: false, reason: 'exchange_failed' };
  }
  const { data, error } = exchangeResponse;
  if (error || !data.session || !data.user) {
    if (data.session) await revokeExchangedLocalSession(client);
    clearAttempt(options.portal, dependencies, true);
    return { ok: false, reason: 'exchange_failed' };
  }

  // Auth-js normally removes its verifier during exchange; enforce the
  // postcondition even if a compatible implementation leaves it behind.
  const verifierRemoved = removeStorageItem(
    dependencies.transactionStorage,
    verifierStorageKey(options.portal, dependencies.supabaseUrl),
  );
  if (!verifierRemoved) {
    await revokeExchangedLocalSession(client);
    return { ok: false, reason: 'oauth_storage_unavailable' };
  }

  return { ok: true, continuation: attempt.continuation };
}

export const portalOAuthFlowInternals = {
  attemptStorageKey,
  attemptTtlMs: ATTEMPT_TTL_MS,
  nonceParameter: OAUTH_NONCE_PARAMETER,
  verifierStorageKey,
};
